import { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UploadFiles from "@/pages/cbb-builder/components/dataSource/UploadFiles";
import { useDocumentUploadTraining } from "@/pages/cbb-builder/hooks/useDocumentUploadTraining";
import { BotContext } from "@/pages/cbb-builder/store";
import { axiosPost } from "@/services/axiosService";
import { userSlice } from "@/redux/User";
import type { State } from "@/redux/Types";
import RemoveConfirmation from "@/pages/cbb-builder/components/RemoveConfirmation";
import ModalWrapper from "@/pages/cbb-builder/components/ModalWrapper";

const TRAINING_DELETE_STATUSES = new Set(["In Progress", "Re-syncing"]);

function buildFileDeleteMessage(pendingDeleteSources: any[]) {
  const hasTrainingInProgress = pendingDeleteSources.some((item) =>
    TRAINING_DELETE_STATUSES.has(item?.status ?? ""),
  );

  if (hasTrainingInProgress) {
    return `${pendingDeleteSources.length > 1 ? "Some selected files are" : "This file is"} still training. Deleting ${pendingDeleteSources.length > 1 ? "them" : "it"} will stop training. This action cannot be undone.`;
  }

  return `Are you sure you want to delete ${pendingDeleteSources.length > 1 ? `${pendingDeleteSources.length} trained files` : "this trained file"}? This action cannot be undone.`;
}

const UploadFilesStep = ({
  restartPolling,
}: {
  restartPolling?: () => void;
}) => {
  const dispatch = useDispatch();
  const [store, storeDispatch] = useContext(BotContext);
  const plan = useSelector((state: State) => state.user.plan);
  const isNewUser = useSelector((state: State) => state.user.isNewUser);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [inProcess, setInProcess] = useState(false);
  const [pendingDeleteSources, setPendingDeleteSources] = useState<any[]>([]);
  const [removeData, setRemoveData] = useState<{
    status: boolean;
    remove: () => void;
  }>({
    status: false,
    remove: () => {},
  });

  const characterLimit =
    plan?.ai_currentLimits?.characters ??
    store.botData?.aiBotConfig?.characterLimit ??
    0;
  const characterUsed = store.botData?.aiBotConfig?.characterUsed ?? 0;

  const {
    handleDocumentTrain,
    handleFileRemove,
    handleFileUpload,
    showProgress,
    uploadProgress,
    uploadStatus,
  } = useDocumentUploadTraining({
    characterLimit,
    characterUsed,
    isOld: !isNewUser,
    plan,
    restartPolling,
  });

  const files = useMemo(
    () => store.botData.dataSources?.documents ?? [],
    [store.botData.dataSources?.documents],
  );
  const trainedFiles = useMemo(
    () => store.botData.aiSource?.documents ?? [],
    [store.botData.aiSource?.documents],
  );

  const deleteTrainDocument = async ({
    source,
    sourceType,
  }: {
    source: any[] | any;
    sourceType?: string;
  }) => {
    const normalizedSources = Array.isArray(source)
      ? source
      : source
        ? [source]
        : [];
    const currentDocuments = store.botData.aiSource?.documents ?? [];
    const deletedSourceKeys = new Set(
      normalizedSources.map((item) =>
        String(item?.id ?? item?.title ?? item?.name ?? ""),
      ),
    );

    try {
      if (!normalizedSources.length) {
        throw new Error("No source found to delete");
      }

      storeDispatch({
        type: "set-aiSource",
        dataKey: "documents",
        dataSet: currentDocuments.filter(
          (doc: any) =>
            !deletedSourceKeys.has(
              String(doc?.id ?? doc?.title ?? doc?.name ?? ""),
            ),
        ),
      });
      setSelectedIds((current) =>
        current.filter(
          (selected) =>
            !deletedSourceKeys.has(
              String(selected?.id ?? selected?.title ?? selected?.name ?? ""),
            ),
        ),
      );

      setInProcess(true);
      const res = await axiosPost("/ai/delete-content", {
        sources: normalizedSources,
        size: 0,
        botCustomId: store.botData.botCustomId,
        botCreatedAt: store.botData.createdAt,
        sourceType: sourceType ?? "documents",
      });

      storeDispatch({
        type: "set-aiSource",
        dataKey: "documents",
        dataSet: currentDocuments.filter(
          (doc: any) =>
            !deletedSourceKeys.has(
              String(doc?.id ?? doc?.title ?? doc?.name ?? ""),
            ),
        ),
        characterUsed: res.data.characterUsed,
      });

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message:
            "Selected data has been successfully removed from the training data.",
        }),
      );
    } catch (err: any) {
      storeDispatch({
        type: "set-aiSource",
        dataKey: "documents",
        dataSet: currentDocuments,
      });

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: err.message,
        }),
      );
    } finally {
      setInProcess(false);
    }
  };

  const handleTrainedFileRemove = async (removeFn: any, data: any) => {
    const normalizedSource = Array.isArray(data?.source)
      ? data.source
      : data?.source
        ? [data.source]
        : [];
    setPendingDeleteSources(normalizedSource);
    setRemoveData({
      status: true,
      remove: async () => {
        await deleteTrainDocument({
          source: normalizedSource,
          sourceType: "documents",
        });
        setPendingDeleteSources([]);
        setRemoveData((prev) => ({ ...prev, status: false }));
      },
    });
  };

  const handleDeleteSelectedTrainedFiles = async (selectedFiles: any[]) => {
    setPendingDeleteSources(selectedFiles);
    setRemoveData({
      status: true,
      remove: async () => {
        await deleteTrainDocument({
          source: selectedFiles,
          sourceType: "documents",
        });
        setPendingDeleteSources([]);
        setRemoveData((prev) => ({ ...prev, status: false }));
      },
    });
  };

  return (
    <>
      <UploadFiles
        plan={plan}
        botData={store.botData}
        isOld={!isNewUser}
        characterLimit={characterLimit}
        characterUsed={characterUsed}
        files={files}
        onFileUpload={handleFileUpload}
        showProgress={showProgress}
        uploadProgress={uploadProgress}
        uploadStatus={uploadStatus}
        onFileRemove={handleFileRemove}
        trainedFiles={trainedFiles}
        handleFileRemove={handleTrainedFileRemove}
        isPostTrainingDelay={false}
        setSelectedIds={setSelectedIds}
        selectedIds={selectedIds}
        setInProcess={setInProcess}
        parentForceReload={() => {}}
        refetchCompany={undefined}
        isRetraining={false}
        setSelectedStatus={undefined}
        retryRejectedLinks={undefined}
        inProcess={inProcess}
        handleDropdowns={undefined}
        handleViewDetails={undefined}
        deleteTrainDocument={deleteTrainDocument}
        onTrainAgent={handleDocumentTrain}
        onDeleteSelectedTrainedFiles={handleDeleteSelectedTrainedFiles}
        fromOnboarding={true}
      />
      {removeData.status && pendingDeleteSources.length > 0 && (
        <ModalWrapper>
          <RemoveConfirmation
            removeData={removeData}
            setRemoveData={setRemoveData}
            isLoading={inProcess}
            title="Delete Trained File"
            message={buildFileDeleteMessage(pendingDeleteSources)}
          />
        </ModalWrapper>
      )}
    </>
  );
};

export default UploadFilesStep;
